import { useEffect, useState } from 'react';
import type { CustomerListItem, FreeIssueReason, ProductListItem, StockWarehouseRef } from '@kassa/shared';
import { api, ApiError } from '../api';
import { NoteField } from '../components/NotesPanel';

const REASONS: [FreeIssueReason, string][] = [
  ['customer', 'Müştəriyə (təsdiqsiz nisyə)'],
  ['internal_use', 'Daxili istifadə'],
  ['sample_gift', 'Nümunə / hədiyyə'],
  ['spoilage', 'Zay'],
  ['expired', 'Müddəti bitib'],
  ['shortage', 'Əskik'],
];

// Satışsız verilmə (KT-61): anbardar səbəb seçir, müştəri (müştərinin üçün), məhsul və miqdar. Qiymət göstərilmir.
export function FreeIssue({ onDone }: { onDone: (notice: string) => void }) {
  const [warehouses, setWarehouses] = useState<StockWarehouseRef[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [reason, setReason] = useState<FreeIssueReason>('customer');
  const [customerId, setCustomerId] = useState('');
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<ProductListItem[]>([]);
  const [lines, setLines] = useState<{ productId: string; name: string; qty: string }[]>([]);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.stockWarehouses().then((w) => {
      setWarehouses(w);
      if (w.length > 0) setWarehouseId(w[0].id);
    }).catch(() => setWarehouses([]));
    api.customers({ limit: 100 }).then((r) => setCustomers(r.items)).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    if (search.trim().length < 2) return setFound([]);
    const t = setTimeout(() => {
      api.products({ q: search.trim(), limit: 10 }).then((r) => setFound(r.items)).catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  function addProduct(p: ProductListItem) {
    setLines((cur) => (cur.some((l) => l.productId === p.id) ? cur : [...cur, { productId: p.id, name: p.name, qty: '1' }]));
    setSearch('');
    setFound([]);
  }

  async function submit() {
    const body = lines.map((l) => ({ productId: l.productId, qty: Number(l.qty) })).filter((l) => l.qty > 0);
    if (!warehouseId || body.length === 0) return setError('Anbar və ən azı bir məhsul seçin');
    if (reason === 'customer' && !customerId) return setError('Müştəri seçin');
    const id = attemptId ?? crypto.randomUUID();
    setAttemptId(id);
    setBusy(true);
    setError(null);
    try {
      const r = await api.freeIssue({ id, warehouseId, reason, customerId: reason === 'customer' ? customerId : null, lines: body, note: note.trim() || null });
      setLines([]);
      setNote('');
      setCustomerId('');
      setAttemptId(null);
      onDone(`${r.displayNo} yazıldı`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verilmə alınmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3>Satışsız verilmə</h3>
      <div className="form-grid">
        <label>
          Anbar
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} disabled={busy}>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.outletName} · {w.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Səbəb
          <select value={reason} onChange={(e) => setReason(e.target.value as FreeIssueReason)} disabled={busy}>
            {REASONS.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {reason === 'customer' && (
          <label>
            Müştəri
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} disabled={busy}>
              <option value="">Seçin</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <label>
        Məhsul axtarışı
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ad, barkod və ya kod" disabled={busy} />
      </label>
      {found.map((p) => (
        <button key={p.id} type="button" className="product-row" onClick={() => addProduct(p)}>
          {p.name}
        </button>
      ))}
      <ul className="product-list">
        {lines.map((l, i) => (
          <li key={l.productId} className="product-row">
            <span className="product-name">{l.name}</span>
            <input
              type="number"
              min="0"
              step="0.001"
              aria-label={`${l.name} miqdar`}
              value={l.qty}
              onChange={(e) => setLines((cur) => cur.map((x, k) => (k === i ? { ...x, qty: e.target.value } : x)))}
              disabled={busy}
            />
          </li>
        ))}
      </ul>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      <NoteField value={note} onChange={setNote} />
      <button type="button" className="btn-primary" onClick={submit} disabled={busy || lines.length === 0}>
        Ver
      </button>
    </section>
  );
}
